import { Observable } from 'rxjs';

/**
 * Abstract HTTP adapter for Leave Management API calls.
 *
 * The consuming application must provide a concrete implementation
 * that bridges to its own HTTP / backend layer (e.g. ErsGenericExtService).
 *
 * Register the implementation at the app root level:
 * ```ts
 * { provide: LmHttpAdapter, useClass: AppLmHttpAdapter }
 * ```
 */
export abstract class LmHttpAdapter {
  /** Generic GET via endpoint config key */
  abstract get<T>(endpoint: string, params?: any[]): Observable<T>;

  /** Generic POST via endpoint config key */
  abstract post<TBody, TResult>(endpoint: string, command: string, body: TBody): Observable<TResult>;

  /** Generic PUT via endpoint config key */
  abstract put<TBody, TResult>(endpoint: string, command: string, body: TBody): Observable<TResult>;

  /** Generic DELETE via endpoint config key */
  abstract delete<T>(endpoint: string, params: any[]): Observable<T>;

  /**
   * GET with additional query-string filters (e.g. ?fromDate=...&status=...).
   * The adapter resolves the base URL from the endpoint key and appends the params.
   */
  abstract getFiltered<T>(endpoint: string, queryParams: Record<string, string>): Observable<T>;

  /** GET that streams a Blob — used for proxied file downloads. */
  abstract getBlob(endpoint: string, urlParams?: any[]): Observable<Blob>;

  /**
   * Lightweight GET that returns file metadata (content-length, content-type)
   * without downloading the full body.
   */
  abstract getFileInfo(
    endpoint: string,
    urlParams?: any[],
  ): Observable<{ contentLength: number | null; contentType: string }>;

  /** Selected tenant observable — provided by the consuming app */
  abstract get selectedUserTenant$(): Observable<{ id: string; [key: string]: any } | null | undefined>;

  /** Upload a file document — provided by the consuming app */
  abstract uploadDocument(
    file: File,
    tenantId: string,
    folder?: string,
  ): Observable<{ fileUrl?: string; fileName?: string }>;
}
